import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, Heart, Loader2, AlertTriangle, 
  Check, X, Utensils, ThumbsUp, ThumbsDown, Target
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { TIER_NAMES } from '@/branding';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LowSaltProgramPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('daily-sodium');
  const [loading, setLoading] = useState(true);
  const [sodiumData, setSodiumData] = useState(null);

  useEffect(() => {
    fetchSodiumAnalysis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSodiumAnalysis = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/myinsul/hypertension/sodium-analysis`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSodiumData(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access restricted');
        navigate('/myinsul');
      } else {
        toast.error('Failed to load sodium analysis');
      }
    } finally {
      setLoading(false);
    }
  };

  const analysis = sodiumData?.analysis;
  const dailyLimit = sodiumData?.daily_limit_mg || 2300;

  // Calculate gauge percentage (capped at 150% for display)
  const getGaugePercentage = () => {
    if (!analysis) return 0;
    const percentage = analysis.percentage_of_limit || 0;
    return Math.min(percentage, 150);
  };

  const getStatusColor = () => {
    if (!analysis) return 'text-gray-500';
    const status = analysis.status;
    if (status === 'within_limit') return 'text-emerald-600';
    if (status === 'borderline') return 'text-amber-600';
    return 'text-red-600';
  };

  const getStatusBgColor = () => {
    if (!analysis) return 'bg-gray-100';
    const status = analysis.status;
    if (status === 'within_limit') return 'bg-emerald-50 border-emerald-200';
    if (status === 'borderline') return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  const tabs = [
    { id: 'daily-sodium', label: 'Daily Average Salt Consumption', icon: Target },
    { id: 'culprit-foods', label: 'My Culprit Foods', icon: ThumbsDown },
    { id: 'good-foods', label: 'My Good Foods', icon: ThumbsUp }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-pink-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/myinsul')}
                className="rounded-full"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-400 to-pink-500 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold bg-gradient-to-r from-red-600 to-pink-600 bg-clip-text text-transparent">
                    Low Salt Program
                  </h1>
                  <p className="text-xs text-muted-foreground">Sodium Management</p>
                </div>
              </div>
            </div>
            <span className="text-xs bg-red-100 text-red-700 px-3 py-1 rounded-full font-medium">
              {TIER_NAMES.cdm}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-8">
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              onClick={() => setActiveTab(tab.id)}
              className={`rounded-full ${
                activeTab === tab.id 
                  ? 'bg-gradient-to-r from-red-500 to-pink-500' 
                  : 'border-red-300 text-red-600 hover:bg-red-50'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="bg-white rounded-3xl shadow-lg p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-red-500 mb-4" />
            <p className="text-muted-foreground">
              Analyzing your sodium intake...
            </p>
          </div>
        ) : !sodiumData?.has_data ? (
          <div className="bg-white rounded-3xl shadow-lg p-12 text-center">
            <Utensils className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Data Available</h3>
            <p className="text-muted-foreground">
              {sodiumData?.message || "Log your meals for a week to see your sodium analysis"}
            </p>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            {/* Daily Average Salt Consumption Tab */}
            {activeTab === 'daily-sodium' && (
              <motion.div
                key="daily-sodium"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                {/* Main Gauge Card */}
                <div className="bg-white rounded-3xl shadow-lg p-8">
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                    <Target className="w-6 h-6 text-red-600" />
                    Daily Average Sodium Consumption
                  </h2>

                  <div className="flex flex-col md:flex-row items-center gap-8">
                    {/* Circular Gauge */}
                    <div className="relative w-64 h-64">
                      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                        {/* Background circle */}
                        <circle
                          cx="50"
                          cy="50"
                          r="42"
                          fill="none"
                          stroke="#e5e7eb"
                          strokeWidth="12"
                        />
                        {/* Progress circle */}
                        <circle
                          cx="50"
                          cy="50"
                          r="42"
                          fill="none"
                          stroke={
                            analysis?.status === 'within_limit' ? '#10b981' :
                            analysis?.status === 'borderline' ? '#f59e0b' : '#ef4444'
                          }
                          strokeWidth="12"
                          strokeLinecap="round"
                          strokeDasharray={`${(getGaugePercentage() / 150) * 264} 264`}
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className={`text-4xl font-bold ${getStatusColor()}`}>
                          {analysis?.daily_average_sodium_mg || 0}
                        </span>
                        <span className="text-sm text-muted-foreground">mg/day</span>
                        <span className="text-xs text-muted-foreground mt-1">
                          {analysis?.percentage_of_limit?.toFixed(1)}% of limit
                        </span>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex-1 space-y-4">
                      <div className={`p-4 rounded-2xl border ${getStatusBgColor()}`}>
                        <div className="flex items-center gap-2 mb-2">
                          {analysis?.status === 'within_limit' ? (
                            <Check className="w-5 h-5 text-emerald-600" />
                          ) : analysis?.status === 'borderline' ? (
                            <AlertTriangle className="w-5 h-5 text-amber-600" />
                          ) : (
                            <X className="w-5 h-5 text-red-600" />
                          )}
                          <span className={`font-bold ${getStatusColor()}`}>
                            {analysis?.status === 'within_limit' ? 'Within Daily Limit' :
                             analysis?.status === 'borderline' ? 'Borderline' : 'Exceeds Limit'}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Daily recommended limit: <strong>{dailyLimit}mg</strong>
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-gray-50 rounded-xl p-4">
                          <p className="text-sm text-muted-foreground">Weekly Total</p>
                          <p className="text-2xl font-bold">{analysis?.total_weekly_sodium_mg || 0}mg</p>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-4">
                          <p className="text-sm text-muted-foreground">Days Analyzed</p>
                          <p className="text-2xl font-bold">{analysis?.days_with_data || 0} days</p>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>

                {/* Date Range */}
                <p className="text-center text-sm text-muted-foreground">
                  Analysis based on {sodiumData.total_entries} meals from {sodiumData.date_range?.start || 'N/A'} to {sodiumData.date_range?.end || 'N/A'}
                </p>
              </motion.div>
            )}

            {/* Culprit Foods Tab */}
            {activeTab === 'culprit-foods' && (
              <motion.div
                key="culprit-foods"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <div className="bg-white rounded-3xl shadow-lg p-8">
                  <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
                    <ThumbsDown className="w-6 h-6 text-red-600" />
                    My Culprit Foods
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Top 5 foods with the highest sodium content from last week
                  </p>

                  {analysis?.high_sodium_foods?.length > 0 ? (
                    <div className="space-y-4">
                      {analysis.high_sodium_foods.slice(0, 5).map((food, index) => (
                        <div 
                          key={`high-sodium-${food.name}-${food.date}`}
                          className="flex items-center gap-4 p-4 bg-red-50 rounded-2xl border border-red-200"
                        >
                          <div className="w-12 h-12 rounded-full bg-red-500 text-white flex items-center justify-center font-bold text-xl">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold">{food.name}</h4>
                            <p className="text-sm text-muted-foreground">
                              {food.meal_type} • {food.date}
                            </p>
                            {food.reason && (
                              <p className="text-xs text-red-600 mt-1">{food.reason}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <span className="text-2xl font-bold text-red-600">{food.sodium_mg}</span>
                            <span className="text-sm text-muted-foreground ml-1">mg</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">
                      No high sodium foods identified
                    </p>
                  )}
                </div>
              </motion.div>
            )}

            {/* Good Foods Tab */}
            {activeTab === 'good-foods' && (
              <motion.div
                key="good-foods"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <div className="bg-white rounded-3xl shadow-lg p-8">
                  <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
                    <ThumbsUp className="w-6 h-6 text-emerald-600" />
                    My Good Foods
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Top 5 foods with the lowest sodium content from last week
                  </p>

                  {analysis?.low_sodium_foods?.length > 0 ? (
                    <div className="space-y-4">
                      {analysis.low_sodium_foods.slice(0, 5).map((food, index) => (
                        <div 
                          key={`low-sodium-${food.name}-${food.date}`}
                          className="flex items-center gap-4 p-4 bg-emerald-50 rounded-2xl border border-emerald-200"
                        >
                          <div className="w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-xl">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold">{food.name}</h4>
                            <p className="text-sm text-muted-foreground">
                              {food.meal_type} • {food.date}
                            </p>
                            {food.reason && (
                              <p className="text-xs text-emerald-600 mt-1">{food.reason}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <span className="text-2xl font-bold text-emerald-600">{food.sodium_mg}</span>
                            <span className="text-sm text-muted-foreground ml-1">mg</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">
                      No low sodium foods identified
                    </p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </main>
    </div>
  );
};

export default LowSaltProgramPage;
